import { FormikInput, FormikProps } from "@modules/ui/components/formikInput"
import { FieldValueChange } from "@src/typings/formik"
import { FieldInputProps } from "formik"
import { DropdownChangeEvent } from "primereact/dropdown"
import { SelectItemOptionsType } from "primereact/selectitem"
import { useEffect, useState } from "react"

export type HUTimeDropdownOrCustomizedTimeInputProps<T, K> = {
  name: string
  label?: string
  dropdownOptions?: SelectItemOptionsType | undefined
  isRequiredInput?: boolean
  getFieldProps: (nameOrOptions: string) => FieldInputProps<any>
  setFieldValue?: FieldValueChange<K>
  disabled?: boolean
  isInvalid: boolean
  error?: React.JSX.Element
} & FormikProps<T, K>

/**
 * A React functional component that renders a dropdown by default, which allows to pick a "custom" option, which allows to enter a custom time value
 * @param {HUTimeDropdownOrCustomizedTimeInputProps} props - The props for the Time Dropdown Or Customized Time Input component.
 * @param {string} name - The name of the field in formik.
 * @param {string} label - The label given to this field.
 * @param {boolean} isRequiredInput - When specified, it specifies if the input should be filled
 * @param {SelectItemOptionsType} dropdownOptions - The list of possible options in the dropdown input.
 * @param {(nameOrOptions: string) => FieldInputProps<any>} getFieldProps - A function that allows to get field input value, name, and event handlers.
 * @param {FieldValueChange<string>} setFieldValue - A function that allows to change the field value.
 * @param {boolean} disabled - When specified, disables the component.
 * @param {boolean} isInvalid - When present, it specifies that the component should have invalid state style.
 * @param {React.JSX.Element} error - A component to display when the field is invalid.
 * @returns {React.JSX.Element} - The rendered component.
 */
export function HUTimeDropdownOrCustomizedTimeInput<T extends string, K>({
  name,
  label,
  dropdownOptions,
  isRequiredInput,
  getFieldProps,
  setFieldValue,
  isInvalid,
  error,
  maskedInputProps,
}: HUTimeDropdownOrCustomizedTimeInputProps<T, K>) {
  const [isCustomizableValue, setIsCustomizableValue] = useState<boolean>(false)

  useEffect(() => {
    if (
      getFieldProps(name).value !== undefined &&
      dropdownOptions &&
      dropdownOptions.findIndex((dropdownOption) => dropdownOption.value === getFieldProps(name).value) < 0
    )
      setIsCustomizableValue(true)
  }, [getFieldProps(name).value])

  return (
    <>
      {isCustomizableValue ? (
        <FormikInput
          flex
          formikStyle={{ display: "flex", flexGrow: 1 }}
          label={label}
          getFieldProps={getFieldProps}
          isInvalid={isInvalid}
          error={error}
          name={name}
          type="maskedInput"
          isRequiredInput={isRequiredInput}
          maskedInputProps={maskedInputProps}
        />
      ) : (
        <FormikInput
          flex
          formikStyle={{ flexGrow: 1 }}
          label={label}
          getFieldProps={getFieldProps}
          isInvalid={isInvalid}
          name={name}
          error={error}
          setFieldValue={setFieldValue}
          type="dropdown"
          isRequiredInput={isRequiredInput}
          dropdownProps={{
            value: dropdownOptions?.find((option) => option.value === getFieldProps(name).value)?.label,
            options: dropdownOptions?.map((option) => option.label),
            onChange: (event: DropdownChangeEvent) => {
              const newValue = dropdownOptions?.find((option) => option.label === event.value)?.value
              setFieldValue?.(name, newValue)
              if (!newValue) {
                setIsCustomizableValue(true)
              }
            },
          }}
        />
      )}
    </>
  )
}
